/**
 * Checkout VIP — flujo de membresía Stripe.
 *
 * El checkout siempre pasa por /api/create-subscription-checkout:
 *  Exterior (todas las variants):
 *    product  prod_UtMbRadxYB3kMP
 *    price_   price_1TtZsFH8xCanmtKXoYfNp4MT  ($39.99/mo)
 *    promo_   promo_1TxbeEH8xCanmtKX3xur5bpt  (50% solo 1er mes)
 *  A/B puede variar video/copy — NUNCA el Stripe price_ ni el promo exterior.
 *
 * Gateways:
 *   AR → VITE_MP_CHECKOUT_VIP_AR (MercadoPago, precio full — SIN descuento/promo)
 *   AR (sin URL MP) → bloquea Stripe; vuelve a /invitacion-premium?country=AR
 *   UNKNOWN / vacío → bloquea Stripe; router geo (/checkout-channel-invite-router)
 *   Exterior → /api/create-subscription-checkout (Stripe, plan_tier: "vip", moneda USD)
 *         → discounts[0][promotion_code] = promo_1Txbe… (50% 1er mes) — solo Stripe
 *         → fan fallback si falla la API
 *
 * Importante: el 50% primer mes es SOLO Stripe exterior. MercadoPago AR nunca
 * usa VITE_MP_CHECKOUT_VIP_DISCOUNT_AR ni promo Stripe.
 */

import { getPlansForCurrency } from "@/config/bioTemplatePremiumPlans";
import { type SupportedLanguage } from "@/config/videoCTAConfig";
import {
  getPremiumInvitePaths,
  getPremiumWebsitePaths,
  PREMIUM_INVITE_FLOW_NAME,
  PREMIUM_WEBSITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import {
  CANONICAL_VIP_AMOUNT_CENTS,
  CANONICAL_VIP_FIRST_MONTH_DISCOUNT_PERCENT,
  CANONICAL_VIP_FIRST_MONTH_PROMO_ID,
  CANONICAL_VIP_PRODUCT_NAME,
  CANONICAL_VIP_STRIPE_PRICE_ID,
  resolveClientMembershipPrice,
} from "@/lib/canonicalVipMembership";
import { getDataFastStripeMetadata } from "@/lib/datafastAnalytics";
import {
  flushPostHogBeforeNavigation,
  getPostHogStripeMetadataPostHog,
  trackStripePayFailurePostHog,
} from "@/lib/posthogAnalytics";
import { internationalStripeCurrency } from "@/lib/stripeBillingCurrency";
import { getMercadoPagoCheckoutVipArUrl } from "@/lib/mercadopagoArCheckoutUrl";
import {
  resolveDirectVipCheckoutUrl,
  vipMembershipFanFallbackUrl,
} from "@/lib/premiumVipCheckoutDestination";
import {
  isMetaInAppBrowser,
  shouldUseNavigationSubscriptionCheckout,
  submitSubscriptionCheckoutViaForm,
} from "@/lib/inAppBrowserCheckout";

export type VipMembershipCheckoutGateway =
  | "stripe"           // Stripe hosted vía /api/create-subscription-checkout (redirect)
  | "stripe_embedded"  // Stripe Embedded Checkout — modal sobre el landing, sin redirect
  | "mercadopago"      // MercadoPago directo (AR)
  | "fan_fallback";    // Reintento vía /invitacion-premium o checkout directo (analytics: fan_fallback)

/**
 * ISO2 para metadata Stripe / gateway.
 * Nunca inventa "US": UNKNOWN / vacío / códigos CF especiales → null
 * (el caller bloquea Stripe y reencamina).
 */
export function billingCountryIso2(raw: string | undefined | null): string | null {
  const t = String(raw ?? "")
    .trim()
    .toUpperCase();
  // Cloudflare specials: XX unknown, T1 Tor, A1 anonymous proxy, A2 satellite
  if (!t || t === "UNKNOWN" || t === "XX" || t === "T1" || t === "A1" || t === "A2") {
    return null;
  }
  if (/^[A-Z]{2}$/.test(t)) return t;
  return null;
}

/** Locale para precios/labels en `getPlansForCurrency` desde el idioma corto del player Mux. */
export function localeForPlansFromMuxLanguage(lang: SupportedLanguage): string {
  const map: Record<SupportedLanguage, string> = {
    es: "es-AR",
    en: "en-US",
    de: "de-DE",
    fr: "fr-FR",
    it: "it-IT",
    pt: "pt-BR",
    nl: "nl-NL",
    tr: "tr-TR",
    ar: "ar-SA",
    pl: "pl-PL",
    ja: "ja-JP",
    ko: "ko-KR",
    id: "id-ID",
  };
  return map[lang] ?? "en-US";
}

export async function startVipMembershipVideoCheckout(options: {
  countryCode: string;
  pathname: string;
  /** Ej. `es-AR` desde Bio; o `localeForPlansFromMuxLanguage` desde Mux. */
  languageForPlans: string;
  /**
   * Tasa de descuento a aplicar (0.10 = 10% off).
   * Se traduce a `discountPercentage` en la API, que aplica un cupón Stripe
   * encima del stripe_price_id pre-creado. No modifica el Price ID.
   * Default: 0 (sin descuento).
   */
  discountRate?: number;
  /**
   * Email del usuario (opcional). Si se provee y el país es AR (MercadoPago),
   * se captura antes de la redirección para habilitar la recuperación de
   * carritos abandonados. No bloquea ni interrumpe el checkout.
   */
  email?: string;
  /**
   * Stripe Price ID (price_…). Exterior VIP ignora overrides y fuerza
   * CANONICAL_VIP_STRIPE_PRICE_ID (todas las variants → mismo price_).
   */
  stripePriceId?: string;
  /**
   * Stripe Promotion Code id (promo_…). Exterior VIP default:
   * CANONICAL_VIP_FIRST_MONTH_PROMO_ID (50% solo primer mes).
   * Se manda como stripe_promotion_code_id → discounts[0][promotion_code].
   */
  stripePromotionCodeId?: string;
  /**
   * Si se provee junto con discountRate>0, indica que el cupón debe ser repeating N meses
   * (para "primer mes con descuento" en templates intl con Mux). Se pasa a la API.
   * Ej: 1 para solo primer mes. Exterior VIP usa promo_ fijo en vez de PROMO{N}_1M.
   */
  discountDurationMonths?: number;
  onIntent?: (detail: { gateway: VipMembershipCheckoutGateway }) => void;
  /**
   * Override del path de cancelación Stripe (relativo, ej. `/pending-approval?offer_membership=1`).
   * Si no se pasa, se usa el default del flow (invitacion-premium / home).
   */
  cancelPath?: string;

  // Template / Dual CTA attribution (from useTemplateSelection + DualCTAVideoLanding)
  // These are propagated to Stripe metadata (and thus to webhook + PostHog).
  // IMPORTANT: use BRAND_SOURCE_KEY for source — never a literal "delfuocampo".
  template?: string;
  template_type?: string;
  dual_ab_bucket?: string;
  dual_variant_id?: string;
  vip_video_asset_id?: number | string | null;
  /**
   * Embedded Checkout (modal sobre la página, sin redirect full-page).
   * Solo aplica al gateway Stripe fuera de in-app browsers (Instagram usa
   * navegación por form). Si la sesión embedded falla por cualquier motivo,
   * se cae solo al flujo hosted de siempre. El caller monta el iframe con
   * el `clientSecret` devuelto (ver StripeEmbeddedCheckoutModal).
   */
  preferEmbedded?: boolean;
}): Promise<{ gateway: VipMembershipCheckoutGateway; clientSecret?: string }> {
  const country = billingCountryIso2(options.countryCode);
  // Exterior VIP: always canonical price_ + 50% first-month promo (all DualCTA variants).
  // A/B may vary Mux/copy only — never product/price/promo.
  const isExteriorVip = Boolean(country && country !== "AR");
  const discountRate = isExteriorVip
    ? CANONICAL_VIP_FIRST_MONTH_DISCOUNT_PERCENT / 100
    : (options.discountRate ?? 0);
  const discountDurationMonths = isExteriorVip ? 1 : options.discountDurationMonths;
  const exteriorPromoId =
    typeof options.stripePromotionCodeId === "string" &&
    options.stripePromotionCodeId.trim().startsWith("promo_")
      ? options.stripePromotionCodeId.trim()
      : CANONICAL_VIP_FIRST_MONTH_PROMO_ID;
  // Exterior: force canonical price_. AR never reaches Stripe path with this force.
  const vipPrice = resolveClientMembershipPrice({
    planTier: "vip",
    explicitPriceId: isExteriorVip
      ? CANONICAL_VIP_STRIPE_PRICE_ID
      : options.stripePriceId,
  });
  const explicitPriceId = isExteriorVip
    ? CANONICAL_VIP_STRIPE_PRICE_ID
    : vipPrice.priceId || CANONICAL_VIP_STRIPE_PRICE_ID;
  const priceSource = isExteriorVip
    ? "canonical_fallback"
    : vipPrice.source === "none"
      ? "canonical_fallback"
      : vipPrice.source;
  const vipMercadoPagoArUrl = getMercadoPagoCheckoutVipArUrl();

  // País ambiguo: no inventar US ni abrir Stripe. Reencaminar al router geo.
  if (!country) {
    console.error(
      "[VIP Checkout] País desconocido — bloqueado Stripe (no UNKNOWN→US).",
      { raw: options.countryCode },
    );
    options.onIntent?.({ gateway: "fan_fallback" });
    await flushPostHogBeforeNavigation(350);
    window.location.assign(
      resolveDirectVipCheckoutUrl("UNKNOWN") || "/checkout-channel-invite-router",
    );
    return { gateway: "fan_fallback" };
  }

  // Stripe international always USD (AR → MercadoPago ARS below).
  void internationalStripeCurrency(country); // assert policy: always USD

  const { vipPlan } = getPlansForCurrency("USD", options.languageForPlans);

  // ── Argentina: MercadoPago obligatorio (nunca Stripe, nunca descuento/promo) ──
  // Precio full del plan VIP en MP. No usar VITE_MP_CHECKOUT_VIP_DISCOUNT_AR:
  // el 50% primer mes es exclusivo de Stripe exterior.
  if (country === "AR") {
    const resolvedArUrl = vipMercadoPagoArUrl;
    if (resolvedArUrl) {
      options.onIntent?.({ gateway: "mercadopago" });

      // Captura del email antes de la redirección a MP (fire-and-forget).
      // Opcional: sin email el checkout sigue igual.
      if (options.email && options.email.includes("@")) {
        const flowName = options.pathname.includes("/invitacion-premium")
          ? PREMIUM_INVITE_FLOW_NAME
          : PREMIUM_WEBSITE_FLOW_NAME;
        const email = options.email.trim().toLowerCase();
        const { setJourneyRecoveryEmail, captureVisitorJourneyRealtime, getJourneyPayloadForServer, getPostHogIdsForServer } =
          await import("@/lib/posthogVisitorJourney");
        setJourneyRecoveryEmail(email);
        captureVisitorJourneyRealtime({
          eventKind: "almost_bought_mp_redirect",
          funnel: flowName,
          productSurface: "checkout",
          recordStep: "mp_checkout_redirect",
          intentLevel: "almost_bought",
          href: resolvedArUrl.slice(0, 500),
          extra: {
            gateway: "mercadopago",
            plan_tier: "vip",
            mp_discount_applied: false,
          },
        });
        const { persistDualCtaCheckoutEmail } = await import("@/lib/dualCtaFunnelTracking");
        persistDualCtaCheckoutEmail({
          email,
          country: "AR",
          flowName,
          provider: "mercadopago",
          amount: vipPlan.price,
          currency: "ARS",
          planTier: "vip",
        });
      } else {
        const { captureVisitorJourneyRealtime } = await import("@/lib/posthogVisitorJourney");
        captureVisitorJourneyRealtime({
          eventKind: "almost_bought_mp_redirect",
          funnel: options.pathname.includes("/invitacion-premium")
            ? PREMIUM_INVITE_FLOW_NAME
            : PREMIUM_WEBSITE_FLOW_NAME,
          productSurface: "checkout",
          recordStep: "mp_checkout_redirect",
          intentLevel: "almost_bought",
          href: resolvedArUrl.slice(0, 500),
          extra: {
            gateway: "mercadopago",
            recovery_eligible: false,
            mp_discount_applied: false,
          },
        });
      }

      // Flush before external MP checkout redirect (same rationale as Stripe).
      await flushPostHogBeforeNavigation(350);
      window.location.assign(resolvedArUrl);
      return { gateway: "mercadopago" };
    }
    console.error(
      "[VIP Checkout] Argentina sin VITE_MP_CHECKOUT_VIP_AR — bloqueado Stripe.",
    );
    options.onIntent?.({ gateway: "fan_fallback" });
    await flushPostHogBeforeNavigation(350);
    window.location.assign("/invitacion-premium?country=AR");
    return { gateway: "fan_fallback" };
  }

  // ── Plan deshabilitado → fan fallback ────────────────────────────────────
  if (vipPlan.isDisabled) {
    options.onIntent?.({ gateway: "fan_fallback" });
    window.location.assign(vipMembershipFanFallbackUrl(country));
    return { gateway: "fan_fallback" };
  }

  // ── Stripe: membership checkout ───────────────────────────────────────────
  // Exterior: price_1TtZs… + promo_1Txbe… (50% 1er mes). No variant price_.
  const onInvitePage = options.pathname.includes("/invitacion-premium");
  const flowName = onInvitePage ? PREMIUM_INVITE_FLOW_NAME : PREMIUM_WEBSITE_FLOW_NAME;
  const planCurrency = "USD";
  const paths = onInvitePage
    ? getPremiumInvitePaths(planCurrency)
    : getPremiumWebsitePaths(planCurrency);
  const origin = window.location.origin;
  const successUrl = `${origin}${paths.successPath}`;
  const cancelOverride =
    typeof options.cancelPath === "string" && options.cancelPath.trim()
      ? options.cancelPath.trim()
      : null;
  const cancelRaw = cancelOverride || paths.cancelPath || "/";
  const cancelUrl = `${origin}${cancelRaw.startsWith("/") ? cancelRaw : `/${cancelRaw}`}`;
  const flow_trace_id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `vip_mux_${Date.now()}`;

  // Stripe bills the Price ID; amount is diagnostic / inline fallback only.
  const amountCents = vipPrice.amountCents ?? CANONICAL_VIP_AMOUNT_CENTS;

  // discountPercentage: entero 0-100. Exterior usa promo_ fijo (no PROMO50_1M).
  const discountPercentage =
    discountRate > 0 && discountRate <= 1
      ? Math.round(discountRate * 100)
      : discountRate > 1
        ? Math.round(discountRate)
        : 0;
  const useExteriorPromo = isExteriorVip && Boolean(exteriorPromoId);

  const buyerEmail =
    options.email && options.email.includes("@")
      ? options.email.trim().toLowerCase()
      : "";

  // Always attempt early capture for Dual/Membership recovery if we have email
  if (buyerEmail) {
    const { persistDualCtaCheckoutEmail } = await import("@/lib/dualCtaFunnelTracking");
    const onInvite = options.pathname.includes("/invitacion-premium");
    const flow = onInvite ? PREMIUM_INVITE_FLOW_NAME : PREMIUM_WEBSITE_FLOW_NAME;
    // For MP it will be called again in path, but idempotent
    try {
      persistDualCtaCheckoutEmail({
        email: buyerEmail,
        country,
        flowName: flow,
        provider: country === "AR" ? "mercadopago" : "stripe",
        amount: vipPlan.price,
        currency: planCurrency.toLowerCase(),
        planTier: "vip",
      });
    } catch {}
  }

  const notifyStripeCheckoutStarted = async (gateway: "stripe" | "fan_fallback") => {
    const { trackDualCtaStripeCheckoutStarted, persistDualCtaCheckoutEmail } =
      await import("@/lib/dualCtaFunnelTracking");
    trackDualCtaStripeCheckoutStarted({
      pathname: options.pathname,
      country,
      amount: vipPlan.price,
      currency: planCurrency,
      gateway,
      hasEmail: Boolean(buyerEmail),
      flowTraceId: flow_trace_id,
      stripePriceId: explicitPriceId,
    });
    if (buyerEmail) {
      persistDualCtaCheckoutEmail({
        email: buyerEmail,
        country,
        flowName,
        provider: "stripe",
        amount: vipPlan.price,
        currency: planCurrency.toLowerCase(),
        planTier: "vip",
      });
    }
  };

  const baseProductName = CANONICAL_VIP_PRODUCT_NAME;
  const productName =
    discountPercentage > 0
      ? discountDurationMonths === 1
        ? `${baseProductName} (${discountPercentage}% OFF primer mes)`
        : `${baseProductName} (${discountPercentage}% OFF)`
      : baseProductName;

  const checkoutPayload = {
    amount: amountCents,
    currency: "usd",
    productName,
    buyerEmail,
    country,
    successUrl,
    cancelUrl,
    intervalCount: 1,
    // Exterior VIP: always price_1TtZs… (prod_UtMb…). Variants do not override.
    stripe_price_id: explicitPriceId,
    // Exterior: prefer fixed promo_ (50% 1er mes) over ephemeral PROMO{N}_1M coupons.
    ...(useExteriorPromo
      ? {
          stripe_promotion_code_id: exteriorPromoId,
          originalAmount: amountCents,
          discountPercentage:
            discountPercentage > 0
              ? discountPercentage
              : CANONICAL_VIP_FIRST_MONTH_DISCOUNT_PERCENT,
          discountDurationMonths: 1,
        }
      : discountPercentage > 0
        ? {
            originalAmount: amountCents,
            discountPercentage,
            ...(discountDurationMonths && discountDurationMonths > 0
              ? { discountDurationMonths }
              : {}),
          }
        : {}),
    metadata: {
      source: BRAND_SOURCE_KEY,
      flow: flowName,
      flow_trace_id,
      pack_id: `${flowName}_vip`,
      plan_tier: "vip",
      product_name: productName,
      stripe_price_id: explicitPriceId,
      membership_price_source: priceSource,
      membership_is_variant: "false",
      ...(useExteriorPromo
        ? {
            promotion_code_id: exteriorPromoId,
            first_month_promo: "true",
          }
        : {}),
      // DataFast + Stripe: recurring membership (MRR). Dual CTA price display is list $/mo.
      revenue_type: "mrr",
      product_type: "subscription",
      billing_interval: "month",
      interval_count: "1",
      ...(options.template ? { template: options.template } : {}),
      ...(options.template_type ? { template_type: options.template_type } : {}),
      ...(options.dual_ab_bucket ? { dual_ab_bucket: options.dual_ab_bucket } : {}),
      ...(options.dual_variant_id ? { dual_variant_id: options.dual_variant_id } : {}),
      ...(options.vip_video_asset_id ? { vip_video_asset_id: String(options.vip_video_asset_id) } : {}),
      ...getPostHogStripeMetadataPostHog(),
      // datafast_visitor_id / datafast_session_id → Stripe Checkout API attribution
      ...getDataFastStripeMetadata(),
    },
  };

  // Instagram / iOS in-app: fetch() falla con "Load failed" — navegación full-page.
  // preferEmbedded esquiva el form-nav de iOS SAFARI (el embedded requiere fetch
  // + iframe, y Stripe lo soporta en mobile Safari) pero NUNCA el de Meta in-app.
  // Si el fetch falla igual (el caso ITP que motivó el form-nav), el catch de
  // abajo cae a submitSubscriptionCheckoutViaForm — no se pierde la conversión.
  const embeddedBypassesFormNav =
    Boolean(options.preferEmbedded) && !isMetaInAppBrowser();
  if (shouldUseNavigationSubscriptionCheckout() && !embeddedBypassesFormNav) {
    options.onIntent?.({ gateway: "stripe" });
    void notifyStripeCheckoutStarted("stripe");

    // Flush before full-page navigation form submit to Stripe (in-app browsers).
    await flushPostHogBeforeNavigation(300);
    await submitSubscriptionCheckoutViaForm({
      amount: checkoutPayload.amount,
      currency: checkoutPayload.currency,
      productName: checkoutPayload.productName,
      buyerEmail: checkoutPayload.buyerEmail,
      country: checkoutPayload.country,
      successUrl: checkoutPayload.successUrl,
      cancelUrl: checkoutPayload.cancelUrl,
      intervalCount: checkoutPayload.intervalCount,
      stripe_price_id: explicitPriceId,
      ...(useExteriorPromo
        ? { stripe_promotion_code_id: exteriorPromoId }
        : {}),
      ...(checkoutPayload.originalAmount
        ? {
            originalAmount: checkoutPayload.originalAmount,
            discountPercentage: checkoutPayload.discountPercentage,
            ...(checkoutPayload.discountDurationMonths
              ? { discountDurationMonths: checkoutPayload.discountDurationMonths }
              : {}),
          }
        : {}),
      metadata: JSON.stringify(checkoutPayload.metadata),
    });
    return { gateway: "stripe" };
  }

  try {
    // Embedded solo fuera de in-app browsers (arriba ya se derivó IG a form-nav).
    const wantEmbedded = Boolean(options.preferEmbedded);
    const res = await fetch("/api/create-subscription-checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        wantEmbedded ? { ...checkoutPayload, ui_mode: "embedded" } : checkoutPayload,
      ),
    });
    const data = (await res.json()) as {
      url?: string;
      client_secret?: string;
      error?: string;
      code?: string;
    };
    if (!res.ok || (!data.url && !data.client_secret)) {
      trackStripePayFailurePostHog({
        stage: "checkout_session_create",
        flow_surface: "vip_membership_video_mux",
        http_status: res.status,
        stripe_error_code: typeof data.code === "string" ? data.code : undefined,
        error_message: data.error || "subscription checkout failed",
        amount_cents: amountCents,
        currency: planCurrency.toLowerCase(),
        plan_tier: "vip",
        flow_trace_id,
      });
      console.error("[startVipMembershipVideoCheckout] subscription checkout failed:", res.status, data);
      options.onIntent?.({ gateway: "fan_fallback" });
      window.location.assign(vipMembershipFanFallbackUrl(country));
      return { gateway: "fan_fallback" };
    }
    options.onIntent?.({ gateway: "stripe" });
    await notifyStripeCheckoutStarted("stripe");

    // Client signal: Stripe Checkout Session was created successfully.
    // Complements the server "stripe_checkout_session_created".
    // Note: actual session id is in the response from API in real impl; here we use flow data.
    if (typeof window !== "undefined" && (window as any).posthog?.capture) {
      (window as any).posthog.capture("stripe_checkout_session_created", {
        plan_tier: "vip",
        flow: flowName,
        flow_trace_id,
        amount: vipPlan.price,
        currency: planCurrency,
        country,
        source: BRAND_SOURCE_KEY,
        ...getPostHogStripeMetadataPostHog(),
      });
    }

    // Embedded: sin redirect — el caller monta el iframe en un modal sobre la
    // página (el video queda de fondo, no se pierde el contexto de conversión).
    if (wantEmbedded && data.client_secret) {
      return { gateway: "stripe_embedded", clientSecret: data.client_secret };
    }

    // Flush pending PostHog events (vip_checkout_started, dual_cta_*, cta_click_vip, etc.)
    // before hard redirect to Stripe. Timeout keeps UX responsive.
    // Anti-saturation: this flush only happens on real checkout navigation.
    await flushPostHogBeforeNavigation(400);
    window.location.assign(data.url!);
    return { gateway: "stripe" };
  } catch (e) {
    trackStripePayFailurePostHog({
      stage: "checkout_session_create",
      flow_surface: "vip_membership_video_mux",
      failure_kind: "network_or_exception",
      error_message: e instanceof Error ? e.message : String(e),
      amount_cents: amountCents,
      currency: planCurrency.toLowerCase(),
      plan_tier: "vip",
      flow_trace_id,
    });
    console.error("[startVipMembershipVideoCheckout] error:", e);
    // fetch tiró y habíamos esquivado el form-nav por preferEmbedded (iOS
    // Safari ITP — el motivo original del form-nav): caer a la navegación
    // full-page hosted, que no depende de fetch. Conversión > embedded.
    if (embeddedBypassesFormNav && shouldUseNavigationSubscriptionCheckout()) {
      options.onIntent?.({ gateway: "stripe" });
      await submitSubscriptionCheckoutViaForm({
        amount: checkoutPayload.amount,
        currency: checkoutPayload.currency,
        productName: checkoutPayload.productName,
        buyerEmail: checkoutPayload.buyerEmail,
        country: checkoutPayload.country,
        successUrl: checkoutPayload.successUrl,
        cancelUrl: checkoutPayload.cancelUrl,
        intervalCount: checkoutPayload.intervalCount,
        stripe_price_id: explicitPriceId,
        // Paridad con el path primario de form-nav de arriba (review 2026-07-16):
        // sin estos campos el descuento primer mes se perdía y se cobraba precio
        // completo con un productName que promete "(X% OFF primer mes)".
        ...(checkoutPayload.originalAmount
          ? {
              originalAmount: checkoutPayload.originalAmount,
              discountPercentage: checkoutPayload.discountPercentage,
              ...(checkoutPayload.discountDurationMonths
                ? { discountDurationMonths: checkoutPayload.discountDurationMonths }
                : {}),
            }
          : {}),
        metadata: JSON.stringify(checkoutPayload.metadata),
      });
      return { gateway: "stripe" };
    }
    options.onIntent?.({ gateway: "fan_fallback" });
    window.location.assign(vipMembershipFanFallbackUrl(country));
    return { gateway: "fan_fallback" };
  }
}
